import { useQuery } from '@tanstack/react-query'

import type {
  OnboardingHealthPayload,
  TransactionsStatsPayload,
  TransactionVolumePayload,
  VesselMetricsPayload,
} from '@ankor-io/common/types/lighthouse'

import { lighthouseService } from '@/api/services'

export const dashboardKeys = {
  vesselMetrics: () => ['dashboard', 'vessel-metrics'] as const,
  onboardingHealth: () => ['dashboard', 'onboarding-health'] as const,
  transactionsStats: () => ['dashboard', 'transactions-stats'] as const,
  transactionVolume: () => ['dashboard', 'transaction-volume'] as const,
}

export function useVesselMetricsQuery(options?: { enabled?: boolean }) {
  return useQuery<VesselMetricsPayload, Error>({
    queryKey: dashboardKeys.vesselMetrics(),
    queryFn: () => lighthouseService.getVesselMetrics(),
    enabled: options?.enabled !== false,
    // These metrics aggregate the whole platform from a DB view; they do not
    // change second-to-second, so avoid refetching on every remount/refocus.
    staleTime: 60_000,
  })
}

export function useOnboardingHealthQuery(options?: { enabled?: boolean }) {
  return useQuery<OnboardingHealthPayload, Error>({
    queryKey: dashboardKeys.onboardingHealth(),
    queryFn: () => lighthouseService.getOnboardingHealth(),
    enabled: options?.enabled !== false,
    // Current totals; monthly granularity, so refetch rarely.
    staleTime: 5 * 60_000,
  })
}

export function useTransactionsStatsQuery(options?: { enabled?: boolean }) {
  return useQuery<TransactionsStatsPayload, Error>({
    queryKey: dashboardKeys.transactionsStats(),
    queryFn: () => lighthouseService.getTransactionsStats(),
    enabled: options?.enabled !== false,
    // Today + 7-day trend; refresh on remount/refocus but not second-to-second.
    staleTime: 60_000,
  })
}

export function useTransactionVolumeQuery(options?: { enabled?: boolean }) {
  return useQuery<TransactionVolumePayload, Error>({
    queryKey: dashboardKeys.transactionVolume(),
    queryFn: () => lighthouseService.getTransactionVolume(),
    enabled: options?.enabled !== false,
    // Daily volume buckets; low churn during the day.
    staleTime: 5 * 60_000,
  })
}